/**
 * Pure helpers for matching a parent-page origin against a campaign's
 * allow list of permitted embed origins.
 *
 * Allow-list entry shapes (validated/parsed at use):
 *   1. Exact origin            "https://acme.com" or "http://localhost:3000"
 *   2. Single-label wildcard   "https://*.acme.com" (matches one subdomain
 *      label only, NOT the apex and NOT multi-level subdomains)
 *   3. Localhost any-port       "http://localhost" (matches any port on
 *      http://localhost)
 *
 * Schemes other than http/https are silently rejected. Malformed entries
 * (unparseable, multi-wildcard, etc.) are silently ignored.
 */

/** Schemes considered valid for embed parent origins. */
const VALID_SCHEMES = new Set(['http:', 'https:']);

/**
 * Internal representation of a parsed allow-list entry.
 */
interface ParsedAllowEntry {
  /** "http:" or "https:" (lowercase, with trailing colon). */
  protocol: string;
  /** Lowercased hostname pattern (may begin with `*.`). */
  hostnamePattern: string;
  /**
   * Port string from the URL (`""` when default). For the special
   * `http://localhost` (no port) entry this is the empty string and
   * indicates "any port allowed on localhost".
   */
  port: string;
  /** True when the entry was the localhost-any-port shorthand. */
  isLocalhostAnyPort: boolean;
}

/**
 * Parse a single allow-list entry into a structured form, returning
 * `null` when the entry is malformed or uses a disallowed scheme.
 */
function parseAllowEntry(entry: string): ParsedAllowEntry | null {
  if (typeof entry !== 'string' || entry.length === 0) return null;

  const trimmed = entry.trim();
  if (trimmed.length === 0) return null;

  // Detect a leading wildcard label and substitute a placeholder host so
  // that URL parsing succeeds. We only support a single leading "*." label.
  let hostnamePattern: string | null = null;
  let urlForParsing = trimmed;
  const wildcardMatch = trimmed.match(/^(https?:\/\/)\*\.([^/?#]+)$/i);
  if (wildcardMatch) {
    const proto = wildcardMatch[1];
    const rest = wildcardMatch[2];
    // Reject multi-wildcard ("*.*.acme.com")
    if (rest.includes('*')) return null;
    hostnamePattern = `*.${rest.toLowerCase()}`;
    urlForParsing = `${proto}placeholder.${rest}`;
  } else if (trimmed.includes('*')) {
    // Wildcards in any other position are not supported.
    return null;
  }

  let url: URL;
  try {
    url = new URL(urlForParsing);
  } catch {
    return null;
  }

  if (!VALID_SCHEMES.has(url.protocol)) return null;

  // Reject anything beyond an origin (path/query/hash) on the entry side.
  if (url.pathname !== '/' && url.pathname !== '') return null;
  if (url.search !== '' || url.hash !== '') return null;

  const finalHostname = hostnamePattern ?? url.hostname.toLowerCase();
  const isLocalhostAnyPort =
    !hostnamePattern && finalHostname === 'localhost' && url.port === '';

  return {
    protocol: url.protocol,
    hostnamePattern: finalHostname,
    port: url.port,
    isLocalhostAnyPort,
  };
}

/**
 * Parse a parent origin string into a structured form.
 */
interface ParsedParentOrigin {
  protocol: string;
  hostname: string;
  port: string;
}

function parseParentOrigin(origin: string): ParsedParentOrigin | null {
  if (typeof origin !== 'string' || origin.length === 0) return null;
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return null;
  }
  if (!VALID_SCHEMES.has(url.protocol)) return null;
  return {
    protocol: url.protocol,
    hostname: url.hostname.toLowerCase(),
    port: url.port,
  };
}

/**
 * Returns true when `parentOrigin` is permitted by the supplied allow
 * list. A null/undefined/empty `allowed` list means "no restriction" and
 * always returns true. See file header for entry-shape rules.
 *
 * @param parentOrigin Origin string from `window.parent`'s ancestor.
 * @param allowed     Optional allow-list (entries are exact origins or
 *                    single-wildcard subdomain patterns).
 */
export function matchesAllowedOrigin(
  parentOrigin: string,
  allowed: string[] | null | undefined
): boolean {
  // No restriction.
  if (!allowed || allowed.length === 0) return true;

  const parent = parseParentOrigin(parentOrigin);
  if (!parent) return false;

  for (const rawEntry of allowed) {
    const entry = parseAllowEntry(rawEntry);
    if (!entry) continue;

    // Scheme must match exactly.
    if (entry.protocol !== parent.protocol) continue;

    // Wildcard subdomain rule.
    if (entry.hostnamePattern.startsWith('*.')) {
      const suffix = entry.hostnamePattern.slice(2); // strip "*."
      // Parent host must be exactly "<one-label>.<suffix>" (no apex,
      // no multi-level). Use indexOf to avoid `endsWith` bypass via
      // "acme.com.attacker.test" against "*.acme.com".
      const parentHost = parent.hostname;
      if (!parentHost.endsWith('.' + suffix)) continue;
      const labelPart = parentHost.slice(0, parentHost.length - suffix.length - 1);
      if (labelPart.length === 0) continue;
      if (labelPart.includes('.')) continue;
      // Port: when the entry specifies a port, parent must match.
      if (entry.port !== '' && entry.port !== parent.port) continue;
      // When entry has no port, accept any parent port (typical for
      // wildcard subdomains in production).
      return true;
    }

    // Localhost any-port shorthand.
    if (entry.isLocalhostAnyPort && parent.hostname === 'localhost') {
      return true;
    }

    // Exact origin match: hostname + port must agree.
    if (entry.hostnamePattern === parent.hostname && entry.port === parent.port) {
      return true;
    }
  }

  return false;
}
